import React, { useState, useEffect, useCallback } from 'react';
import MathHtml from '../../components/shared/MathHtml';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getTest, submitTest } from '../../api';

function toHtml(s) { const t = s || ''; return t.indexOf('<') >= 0 ? t : t.split('\n').join('<br>'); }
function HtmlDiv(p) { return React.createElement(MathHtml, { tag: 'div', style: p.style, html: toHtml(p.html) }); }
function HtmlSpan(p) { return React.createElement(MathHtml, { tag: 'span', style: p.style, html: toHtml(p.html) }); }

function Timer({ limitMinutes, onExpire }) {
  const [secs, setSecs] = useState(limitMinutes * 60);
  useEffect(() => {
    const t = setInterval(() => setSecs(s => {
      if (s <= 1) { clearInterval(t); onExpire(); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [onExpire]);
  const m = Math.floor(secs / 60), s = secs % 60;
  const pct = secs / (limitMinutes * 60);
  const color = pct < 0.2 ? '#EF4444' : pct < 0.4 ? '#F59E0B' : '#10B981';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px', background: pct < 0.2 ? '#FEF2F2' : '#F8FAFC', border: `2px solid ${color}`, borderRadius: 8 }}>
      <span>T</span>
      <span style={{ fontWeight: 800, color, fontSize: 20, fontFamily: 'Sora' }}>
        {String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
      </span>
    </div>
  );
}

const OPTS = ['A', 'B', 'C', 'D'];

export default function TakeTest() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [showPanel, setShowPanel] = useState(true);

  const { data: test, isLoading, error } = useQuery({
    queryKey: ['test', id],
    queryFn: () => getTest(id).then(r => r.data),
  });

  const submitMut = useMutation({
    mutationFn: (ans) => submitTest(id, {
      answers: Object.entries(ans).map(([qid, answer]) => ({ question_id: parseInt(qid), answer }))
    }),
    onSuccess: () => {
      setSubmitted(true);
      navigate(`/student/test/${id}/result`);
    },
    onError: (e) => {
      // Backend commits the test before sending email; an SMTP hang or 504 here
      // does NOT mean the test failed to submit. Navigate to the result page —
      // if it really didn't save, TestResult will surface that cleanly.
      if (e?.response?.status === 400 || e?.response?.status === 504 || !e?.response) {
        setSubmitted(true);
        navigate(`/student/test/${id}/result`);
      }
    },
  });

  const handleSubmit = useCallback(() => {
    if (submitted || submitMut.isPending) return;
    submitMut.mutate(answers);
  }, [answers, submitted, submitMut]);

  if (isLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}><div className="spinner" /></div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#EF4444' }}>{error.response?.data?.detail || 'Error loading test'}</div>;

  const q = test.questions[current];
  const total = test.questions.length;
  const attempted = Object.keys(answers).length;
  const notAttempted = total - attempted;

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <div style={{ background: '#1E1B4B', color: 'white', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
        <div>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 15 }}>{test.title}</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>{attempted} answered · {notAttempted} remaining</div>
        </div>
        <Timer limitMinutes={test.time_limit_minutes} onExpire={handleSubmit} />
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowPanel(!showPanel)} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'white', cursor: 'pointer', fontSize: 13 }}>
            Questions
          </button>
          <button onClick={() => { if (window.confirm(`Submit? ${notAttempted} unanswered.`)) handleSubmit(); }}
            disabled={submitMut.isPending}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#10B981', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            {submitMut.isPending ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', maxWidth: 1100, margin: '0 auto', padding: 20, gap: 20 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Question {current + 1} of {total}</span>
            <div style={{ flex: 1, height: 4, background: 'var(--border)', borderRadius: 2 }}>
              <div style={{ height: '100%', borderRadius: 2, background: 'var(--primary)', width: `${((current + 1) / total) * 100}%`, transition: 'width 0.3s' }} />
            </div>
          </div>

          <div className="card">
            {q.passage_text && (
              <div style={{ background: '#F8FAFC', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 20, fontSize: 14, lineHeight: 1.9 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Passage</div>
                <HtmlDiv html={q.passage_text} />
              </div>
            )}

            <HtmlDiv html={q.question_text} style={{ fontSize: 16, lineHeight: 1.8, marginBottom: 20, fontWeight: 500 }} />
            {q.question_image_url && <img src={q.question_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 16, border: '1px solid var(--border)' }} />}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {OPTS.map(opt => {
                const val = q[`option_${opt.toLowerCase()}`];
                const selected = answers[q.id] === opt;
                return (
                  <button key={opt} onClick={() => setAnswers(a => ({ ...a, [q.id]: opt }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 10, width: '100%', border: `2px solid ${selected ? '#4F46E5' : '#E2E8F0'}`, background: selected ? '#EEF2FF' : 'white', cursor: 'pointer', textAlign: 'left', transition: 'all 0.12s' }}>
                    <span style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: selected ? '#4F46E5' : '#F1F5F9', color: selected ? 'white' : '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>{opt}</span>
                    <HtmlSpan html={val} style={{ fontSize: 15, color: selected ? '#3730A3' : '#0F172A', fontWeight: selected ? 500 : 400 }} />
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAnswers(a => { const n = { ...a }; delete n[q.id]; return n; })} style={{ visibility: answers[q.id] ? 'visible' : 'hidden' }}>Clear</button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-ghost" disabled={current === 0} onClick={() => setCurrent(c => c - 1)}>Prev</button>
                <button className="btn btn-primary" disabled={current === total - 1} onClick={() => setCurrent(c => c + 1)}>Next</button>
              </div>
            </div>
          </div>
        </div>

        {showPanel && (
          <div style={{ width: 220, flexShrink: 0 }}>
            <div className="card" style={{ padding: 16, position: 'sticky', top: 80 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Question Map</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4 }}>
                {test.questions.map((qq, i) => (
                  <button key={i} onClick={() => setCurrent(i)} style={{ width: 34, height: 34, borderRadius: 6, border: current === i ? '2px solid #4F46E5' : '1px solid #E2E8F0', background: answers[qq.id] ? '#D1FAE5' : current === i ? '#EEF2FF' : '#F8FAFC', color: current === i ? '#4F46E5' : '#334155', fontWeight: current === i ? 700 : 400, fontSize: 12, cursor: 'pointer' }}>{i + 1}</button>
                ))}
              </div>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: 13 }}>
                <div>Answered: <strong>{attempted}</strong></div>
                <div>Remaining: <strong>{notAttempted}</strong></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
